import { emptyRecords, type BlobStore, type OutboxEntry, type PersistedState, type SyncMeta, type SyncStorage } from '@clim/client-core';
import type { SyncChange, SyncEntity } from '@clim/domain';
import { openDB, type DBSchema, type IDBPDatabase } from 'idb';

interface ClimDB extends DBSchema {
  records: { key: [string, string]; value: { entity: SyncEntity; id: string; record: unknown } };
  outbox: { key: number; value: OutboxEntry };
  meta: { key: string; value: SyncMeta };
  blobs: { key: string; value: Blob };
}

/**
 * IndexedDB persistence for the web app (Chrome first). One database per tenant+user so that
 * a user switch on a shared computer never mixes data or pushes someone else's outbox.
 * Every operation is a single IndexedDB transaction (atomic).
 */
export class IndexedDbStorage implements SyncStorage {
  private dbp: Promise<IDBPDatabase<ClimDB>>;

  constructor(readonly name: string) {
    this.dbp = openDB<ClimDB>(name, 2, {
      upgrade(db, oldVersion) {
        if (oldVersion < 1) {
          db.createObjectStore('records', { keyPath: ['entity', 'id'] });
          db.createObjectStore('outbox', { keyPath: 'seq' });
          db.createObjectStore('meta');
        }
        // v2: contents of photos / documents waiting to be sent
        if (oldVersion < 2) db.createObjectStore('blobs');
      },
    });
  }

  /** local file contents (same database: cleared with the user's data) */
  readonly blobs: BlobStore = {
    put: async (id, data) => {
      await (await this.dbp).put('blobs', new Blob([data as BlobPart]), id);
    },
    read: async (id, start = 0, end) => {
      const b = await (await this.dbp).get('blobs', id);
      return b ? new Uint8Array(await b.slice(start, end).arrayBuffer()) : null;
    },
    delete: async (id) => {
      await (await this.dbp).delete('blobs', id);
    },
    list: async () => (await (await this.dbp).getAllKeys('blobs')) as string[],
  };

  async load(): Promise<PersistedState | null> {
    const db = await this.dbp;
    const tx = db.transaction(['records', 'outbox', 'meta'], 'readonly');
    const [rows, outbox, meta] = await Promise.all([
      tx.objectStore('records').getAll(),
      tx.objectStore('outbox').getAll(),
      tx.objectStore('meta').get('meta'),
    ]);
    if (!meta) return null;
    const records = emptyRecords();
    for (const r of rows) (records[r.entity] as Record<string, unknown>)[r.id] = r.record;
    return { records, outbox, meta };
  }

  async applyPull(changes: SyncChange[], meta: SyncMeta, reset: boolean): Promise<void> {
    const db = await this.dbp;
    const tx = db.transaction(['records', 'meta'], 'readwrite');
    const store = tx.objectStore('records');
    if (reset) await store.clear();
    for (const c of changes) {
      if (c.op === 'upsert' && c.record) await store.put({ entity: c.entity, id: c.id, record: c.record });
      else await store.delete([c.entity, c.id]);
    }
    await tx.objectStore('meta').put(meta, 'meta');
    await tx.done;
  }

  async addOutbox(entry: OutboxEntry, meta: SyncMeta): Promise<void> {
    const db = await this.dbp;
    const tx = db.transaction(['outbox', 'meta'], 'readwrite');
    await tx.objectStore('outbox').put(entry);
    await tx.objectStore('meta').put(meta, 'meta');
    await tx.done;
  }

  async ackOutbox(removed: number[], updated: OutboxEntry[], meta: SyncMeta): Promise<void> {
    const db = await this.dbp;
    const tx = db.transaction(['outbox', 'meta'], 'readwrite');
    for (const seq of removed) await tx.objectStore('outbox').delete(seq);
    for (const e of updated) await tx.objectStore('outbox').put(e);
    await tx.objectStore('meta').put(meta, 'meta');
    await tx.done;
  }

  async setMeta(meta: SyncMeta): Promise<void> {
    const db = await this.dbp;
    await db.put('meta', meta, 'meta');
  }

  async clear(): Promise<void> {
    const db = await this.dbp;
    const tx = db.transaction(['records', 'outbox', 'meta'], 'readwrite');
    await Promise.all([tx.objectStore('records').clear(), tx.objectStore('outbox').clear(), tx.objectStore('meta').clear()]);
    await tx.done;
  }
}
